<template>
  <div class="mx-auto mt-4 w-full max-w-app px-3">
    <portfolio-actions
      :is-loading="isLoading"
      :is-recalculating="isRecalculating"
      @recalculate="handleRecalculate"
    >
      <template #title-suffix>
        <filter-toggle
          v-if="availablePlatforms.length > 0"
          v-model="filtersOpen"
          :selected="selectedPlatforms.length"
          :available="availablePlatforms.length"
        />
      </template>
    </portfolio-actions>

    <platform-filter
      v-if="filtersOpen && availablePlatforms.length > 0"
      class="mb-6"
      :available="availablePlatforms"
      :selected="selectedPlatforms"
      @toggle="togglePlatform"
      @toggle-all="toggleAllPlatforms"
    />

    <div v-if="viewState === 'LOADING'">
      <skeleton-loader type="card" class="mb-6" />
      <skeleton-loader type="table" :rows="10" :columns="5" />
    </div>

    <AlertMessage v-else-if="viewState === 'ERROR'" variant="danger">
      {{ error }}
    </AlertMessage>

    <AlertMessage v-else-if="viewState === 'EMPTY'" variant="info">
      No portfolio summary data found.
    </AlertMessage>

    <template v-else>
      <AlertMessage
        v-if="showRecalculationMessage"
        variant="info"
        dismissible
        class="mt-4"
        @dismiss="recalculationMessage = ''"
      >
        {{ recalculationMessage }}
      </AlertMessage>

      <header v-if="latestSummary" class="portfolio-headline">
        <h1>
          <span :class="headlineFlashClass">
            <span>{{ headlineAmount[0] }}</span>
            <span class="headline-cents">{{ headlineAmount[1] }}</span>
          </span>
        </h1>
        <div class="headline-meta">
          <range-change-header
            v-if="rangeChange"
            :key="`${rangeChange.range}:${rangeChange.platforms}`"
            :amount="rangeChange.changeAmount"
            :percent="rangeChange.changePercent"
            :range="rangeChange.range"
          />
          <span v-if="staleAsOf" class="headline-asof">as of {{ staleAsOf }}</span>
        </div>
      </header>

      <div class="chart-frame">
        <portfolio-chart :key="chartKey" :data="activeChartData" />
        <loading-veil v-if="isRangeLoading" message="Loading chart" />
      </div>

      <AlertMessage v-if="rangeError" variant="warning" class="mt-3">
        Could not load the {{ selectedRange }} chart range: {{ rangeError }}
      </AlertMessage>

      <div
        class="mt-3 mb-6 flex flex-wrap items-center justify-between gap-3 md:mb-10 md:w-[80.8%]"
        data-testid="chart-controls"
      >
        <chart-range-filter :selected="selectedRange" @select="selectedRange = $event" />
        <chart-mode-toggle :selected="selectedBenchmarks" @select="selectBenchmark" />
      </div>

      <segmented-switch
        class="mb-3"
        label="Interval"
        :options="INTERVAL_OPTIONS"
        :selected="interval"
        :announcement="announcement"
        @select="choose"
      />

      <template v-if="view === 'monthly'">
        <monthly-profit-calendar
          :key="selectedPlatforms.join()"
          :model="monthly"
          :error="historyError"
          :refreshing="isFetchingHistory"
          class="mb-2 md:mb-10"
          @retry="retryHistory"
        />
        <monthly-summary-table v-if="monthly !== null" :model="monthly" />
      </template>

      <div v-else class="relative" :aria-busy="view === 'pending' || undefined">
        <daily-profit-calendar
          :key="selectedPlatforms.join()"
          :rows="dailyCalendarRows"
          :error="calendarError"
          :history="calendarHistory"
          :inert="view === 'pending'"
          class="mb-2 md:mb-10"
          @load-history="loadCalendarHistory"
        />
        <daily-summary-table :summaries="reversedSummaries" :inert="view === 'pending'" />
        <loading-veil v-if="view === 'pending'" delayed message="Loading months" />
      </div>

      <div v-if="isFetching && !isMonthly" class="mt-4 text-center">
        <skeleton-loader type="text" :lines="2" />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { defineAsyncComponent, computed, h, ref, watch } from 'vue'
import { useInfiniteScroll, useLocalStorage, useWindowSize } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import { usePortfolioSummaryQuery } from '../composables/use-portfolio-summary-query'
import {
  usePortfolioChart,
  usePerformanceChart,
  useBenchmarkSelection,
  type BenchmarkKey,
  type ChartBenchmark,
} from '../composables/use-portfolio-chart'
import { useConfirm } from '../composables/use-confirm'
import { usePlatformFilter } from '../composables/use-platform-filter'
import { useChartRange } from '../composables/use-time-range'
import { useAuthState } from '../composables/use-auth-state'
import { formatDateToString } from '../composables/use-quick-dates'
import { useNumberTransition } from '../composables/use-number-transition'
import {
  type SummaryInterval,
  useStoredInterval,
  useSummaryInterval,
} from '../composables/use-summary-interval'
import { useFlashOnChange } from '../composables/use-flash-on-change'
import PortfolioActions from './portfolio/portfolio-actions.vue'
import ChartRangeFilter from './portfolio/chart-range-filter.vue'
import ChartModeToggle from './portfolio/chart-mode-toggle.vue'
import RangeChangeHeader from './portfolio/range-change-header.vue'
import DailyProfitCalendar from './portfolio/daily-profit-calendar.vue'
import DailySummaryTable from './portfolio/daily-summary-table.vue'
import MonthlyProfitCalendar from './portfolio/monthly-profit-calendar.vue'
import MonthlySummaryTable from './portfolio/monthly-summary-table.vue'
import SegmentedSwitch from './shared/segmented-switch.vue'
import SkeletonLoader from './shared/skeleton-loader.vue'
import LoadingSpinner from './shared/loading-spinner.vue'
import LoadingVeil from './shared/loading-veil.vue'
import PlatformFilter from './shared/platform-filter.vue'
import FilterToggle from './shared/filter-toggle.vue'
import AlertMessage from './shared/alert-message.vue'
import { transactionsService } from '../services/api'
import { buildMonthlyProfit } from '../services/monthly-profit'
import { STORAGE_KEYS } from '../constants'
import { REFETCH_INTERVALS } from '../constants/api'
import { formatCurrencyWithSymbol, formatDate, splitCents } from '../utils/formatters'

const INTERVAL_OPTIONS: { value: SummaryInterval; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'monthly', label: 'Monthly' },
]

const PortfolioChart = defineAsyncComponent({
  loader: () => import('./portfolio/portfolio-chart.vue'),
  loadingComponent: () => h(LoadingSpinner, { class: 'min-h-48' }),
  errorComponent: () =>
    h(
      AlertMessage,
      { variant: 'warning' },
      () => 'Could not load the chart. Refresh the page to try again.'
    ),
})

type ViewState = 'LOADING' | 'ERROR' | 'EMPTY' | 'SUCCESS'

const { isAuthenticated } = useAuthState()

const { data: platformsData } = useQuery({
  queryKey: ['transaction-platforms'],
  queryFn: () => transactionsService.getPlatforms(),
  enabled: isAuthenticated,
  refetchInterval: REFETCH_INTERVALS.PLATFORMS,
})

const availablePlatforms = computed(() => platformsData.value ?? [])

const { selectedPlatforms, togglePlatform, toggleAllPlatforms } = usePlatformFilter(
  STORAGE_KEYS.SELECTED_SUMMARY_PLATFORMS,
  availablePlatforms
)

const filtersOpen = useLocalStorage(STORAGE_KEYS.SUMMARY_FILTERS_OPEN, true)

const selectedRange = useChartRange()

const interval = useStoredInterval()
const isMonthly = computed(() => interval.value === 'monthly')

const {
  summaries,
  chartSummaries,
  performanceSummaries,
  benchmarks,
  rangeChange,
  dailyCalendarRows,
  calendarHistory,
  loadCalendarHistory,
  reversedSummaries,
  isLoading,
  isRecalculating,
  isFetching,
  isRangeLoading,
  error,
  rangeError,
  calendarError,
  monthlyRows,
  historyError,
  isFetchingHistory,
  retryHistory,
  recalculationMessage,
  recalculate,
  fetchSummaries,
  hasMoreData,
} = usePortfolioSummaryQuery(selectedPlatforms, selectedRange, isMonthly)

const { processedChartData } = usePortfolioChart(chartSummaries)

const selectedBenchmarks = useBenchmarkSelection()

const activeBenchmarks = computed<ChartBenchmark[]>(() =>
  benchmarks.value.filter(
    benchmark => selectedBenchmarks.value.includes(benchmark.key) && benchmark.points.length > 0
  )
)

const { performanceChartData } = usePerformanceChart(performanceSummaries, activeBenchmarks)

const activeChartData = computed(() => performanceChartData.value ?? processedChartData.value)

const selectBenchmark = (key: BenchmarkKey) => {
  selectedBenchmarks.value = selectedBenchmarks.value.includes(key)
    ? selectedBenchmarks.value.filter(selected => selected !== key)
    : [...selectedBenchmarks.value, key]
}

const { confirm } = useConfirm()

const { width } = useWindowSize()
const resizeCount = ref(0)

watch(width, () => {
  resizeCount.value++
})

const chartKey = computed(() =>
  [resizeCount.value, ...activeBenchmarks.value.map(benchmark => benchmark.key)].join('-')
)

const viewState = computed<ViewState>(() => {
  if (isLoading.value) return 'LOADING'
  if (error.value) return 'ERROR'
  if (!summaries.value || summaries.value.length === 0) return 'EMPTY'
  return 'SUCCESS'
})

const showRecalculationMessage = computed(() => !!recalculationMessage.value)

const latestSummary = computed(() => reversedSummaries.value[0] ?? null)

const headlineValue = computed(() =>
  viewState.value === 'SUCCESS' ? (latestSummary.value?.totalValue ?? null) : null
)
const animatedHeadlineValue = useNumberTransition(headlineValue, selectedPlatforms)
const headlineFlashClass = useFlashOnChange(headlineValue, selectedPlatforms)
const headlineAmount = computed(() =>
  splitCents(formatCurrencyWithSymbol(animatedHeadlineValue.value))
)

const today = () => formatDateToString(new Date())

const staleAsOf = computed(() => {
  const date = latestSummary.value?.date
  if (!date || date === today()) return null
  return formatDate(date)
})

const monthly = computed(() => monthlyRows.value && buildMonthlyProfit(monthlyRows.value, today()))

const { view, announcement, choose } = useSummaryInterval(interval, {
  model: monthly,
  error: historyError,
  platforms: selectedPlatforms,
})

useInfiniteScroll(
  window,
  async () => {
    if (isFetching.value) return
    await fetchSummaries()
  },
  { distance: 100, canLoadMore: () => !isMonthly.value && !!hasMoreData?.value }
)

const handleRecalculate = async () => {
  const shouldProceed = await confirm({
    title: 'Recalculate Portfolio Data',
    message:
      'This will delete all current summary data and recalculate it from scratch. This operation may take some time. Continue?',
    confirmText: 'Recalculate',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (shouldProceed) {
    await recalculate()
  }
}
</script>

<style scoped>
.chart-frame {
  position: relative;
  min-height: 12rem;
}

.portfolio-headline {
  margin-bottom: 1.5rem;
}

.portfolio-headline h1 {
  margin: 0;
  line-height: 1.05;
}

.headline-cents {
  font-size: 0.5em;
  font-weight: 500;
  color: var(--color-ink-soft);
}

.headline-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem 0.75rem;
  margin-top: 0.375rem;
}

.headline-asof {
  font-size: var(--text-sm);
  color: var(--color-ink-soft);
}
</style>
